/**
 * @file embed-popup.tsx
 * @module EmbedPopup
 * @description Galvenā uznirstošā loga komponente iegultajai (Mapbox GL) kartei.
 *              Attēlo objekta detaļas, galveni ar ikonu navigāciju (vairākiem
 *              objektiem vienā punktā), lineāro referenci un atribūtu sarakstu.
 *              Pozicionēts kā Mapbox GL Popup virs atlasītā objekta.
 */

import type { MapGeoJSONFeature } from "maplibre-gl";
import { useContext, useEffect, useState } from "react";
import EmbedPopupHeader from "./embed-popup-header";
import EmbedPopupLinearRef from "./embed-popup-linear-ref";
import EmbedPopupDetails, { getValue } from "./embed-popup-details";
import { EmbedMapContext } from "../embed-map/embed-map";

/**
 * Renders the popup content for a set of clicked map features.
 * Handles feature switching when multiple features are stacked,
 * displays event type titles, linear references, and detail attributes.
 *
 * @param features - Array of clicked MapGeoJSONFeature objects (deduplicated)
 */
export default function EmbedPopup({ features }: { features: MapGeoJSONFeature[] }) {
	const [expand, setExpand] = useState(false);
	const [currentFeature, setCurrentFeature] = useState(features[0]);
	const { map, layers } = useContext(EmbedMapContext);

	const isDetour = !!currentFeature.layer.id.includes("detour");

	useEffect(() => {
		setCurrentFeature(features[0]);
	}, [features]);

	useEffect(() => {
		setExpand(false);
	}, [currentFeature]);

	// Look for linked point feature for detours
	const getLinkedFeature = () => {
		if (isDetour) {
			const ids = layers.filter((l) => l.id.includes("point")).map((l) => l.id);
			const linkedFeature = map?.queryRenderedFeatures({ layers: ids }).find((f) => f.id === currentFeature.id);
			return linkedFeature ?? currentFeature;
		}
		return currentFeature;
	};

	const getTitle = () => {
		if (["road_layer", "poly_layer"].includes(currentFeature.layer.id)) {
			switch (currentFeature.properties["road_conditions"]) {
				case "satisfying":
					return "Apmierinoši apstākļi";
				case "unsatisfying":
					return "Apgrūtināti apstākļi";
				case "very_unsatisfying":
					return "Ļoti apgrūtināti apstākļi";
				case "extreme":
					return "Ekstrēmi apstākļi";
				default:
					return "Nezināmi apstākļi";
			}
		}

		switch (currentFeature.layer.id) {
			case "cms_device":
				return "CMS iekārta";
			case "cms_device_2":
				return "Kamera";
			case "lau-point":
				return "Uzturētaju tehnika";
			case "road-work-point-blue":
			case "road-work-line-blue":
			case "road-work-line-blue-alt":
			case "road-work-point-blue-alt":
				return "Plānotie būvdarbi";
			case "road-work-point-black":
			case "road-work-line-black":
				return "Būvdarbu tehnoloģiskais pārtraukums";
			case "event-detour-layer-line":
			case "event-detour-layer-line-alt":
				const linkedFeature = getLinkedFeature();
				return (
					<div>
						<p>Apbraukšanas maršruts</p>
						<p>{getValue(linkedFeature.properties["datex_enum"], "datex_enum", linkedFeature)}</p>
					</div>
				);
			default:
				return getValue(currentFeature.properties["datex_enum"], "datex_enum", currentFeature);
		}
	};

	const lr = getLinkedFeature()
		.properties.linear_reference?.split(";")
		.map((lr: string) => lr.trim()) as string[];

	let lrList = lr;

	if (lrList?.length > 2 && !expand) {
		lrList = [lrList[0], lrList[1]];
	}

	return (
		<>
			<EmbedPopupHeader selected={(f) => setCurrentFeature(f)} features={features} />
			<div>
				<h3 className="text-xl font-semibold text-[#303030] mb-2">{getTitle()}</h3>
				<div className="flex divide-x-2">
					{currentFeature?.layer.type !== "fill" && lr && lrList.filter((lr) => lr !== "").length > 0 && (
						<div className="flex flex-col gap-2 pr-2">
							{lrList.map((lr, i) => {
								return <EmbedPopupLinearRef key={i} linRef={lr} />;
							})}
						</div>
					)}

					{currentFeature.properties["geographic_name"] && (
						<div className="flex gap-1 pl-2 items-center 	">
							<span className="material-icons text-[#4C4E4F]">location_on</span>
							<p className="text-base font-normal">{currentFeature.properties["geographic_name"]}</p>
						</div>
					)}
				</div>
				{lr?.length > 2 && (
					<button className="flex gap-1 items-center text-sm font-normal focus:outline-none select-none mt-0.5" onClick={() => setExpand(!expand)}>
						<p className="underline">{!expand ? "Rādīt visu" : "Rādīt mazāk"}</p>
						<span className={`material-icons text-lg ${expand && "rotate-180"}`}>arrow_drop_down</span>
					</button>
				)}
			</div>
			<div className="w-full h-[1px] bg-[#d9d9d9] mb-3 mt-4"></div>
			<EmbedPopupDetails feature={currentFeature} />
		</>
	);
}
